/**
 * BillingPage — /billing (any authenticated user)
 *
 * Shows license status and Commerce payment history.
 * Uses commerce_license/role (not commerce_subscription) so contributors
 * can see their billing info even when they don't own the subscription.
 */
import {
  useLicenses,
  usePaymentHistory,
} from '@/hooks/useBilling';
import { useAuth } from '@/components/auth/AuthContext';
import { cn } from '@/lib/utils';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

const STATE_LABELS = {
  draft:     'Upcoming',
  completed: 'Paid',
  refunded:  'Refunded',
  voided:    'Voided',
  canceled:  'Canceled',
  new:       'New',
};

const STATE_STYLES = {
  draft:     'bg-sky-50 text-sky-700 border-sky-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  refunded:  'bg-amber-50 text-amber-700 border-amber-200',
  voided:    'bg-neutral-100 text-neutral-500 border-neutral-200',
  canceled:  'bg-neutral-100 text-neutral-500 border-neutral-200',
  new:       'bg-sky-50 text-sky-700 border-sky-200',
};

function stateChip(state) {
  return (
    <span className={cn(
      'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium',
      STATE_STYLES[state] ?? 'bg-neutral-100 text-neutral-500 border-neutral-200'
    )}>
      {STATE_LABELS[state] ?? state}
    </span>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const DRUPAL_BASE = (
  import.meta.env.VITE_DRUPAL_DIRECT_URL ||
  import.meta.env.VITE_DRUPAL_BASE_URL ||
  ''
).replace(/\/?$/, '');

function LicenseCard({ licenses, isLoading, isError, uid }) {
  const stateColor = (s) =>
    s === 'active'   ? 'text-emerald-700' :
    s === 'canceled' ? 'text-neutral-400' :
    'text-amber-700';

  return (
    <div className="rounded-2xl border border-neutral-200 bg-paper p-5 space-y-4">
      <h2 className="text-sm font-semibold text-ink">Subscription</h2>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2].map((i) => <div key={i} className="h-8 animate-pulse rounded-lg bg-parchment" />)}
        </div>
      ) : isError ? (
        <p className="text-sm text-neutral-500">Unable to load subscription.</p>
      ) : !licenses?.length ? (
        <p className="text-sm text-neutral-500">No active subscription found.</p>
      ) : (
        licenses.map((l) => {
          const rows = [
            ['Plan',    l.tier ?? '—'],
            ['Billing', l.period === 'annual' ? 'Annual' : 'Monthly'],
            ['Amount',  l.price?.formatted ?? '—'],
            ['Since',   formatDate(l.granted)],
            ['Expires', l.expires ? formatDate(l.expires) : 'Unlimited'],
          ];
          return (
            <div key={l.id}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">License</span>
                <span className={`text-xs font-medium capitalize ${stateColor(l.state)}`}>{l.state}</span>
              </div>
              <div className="divide-y divide-neutral-200">
                {rows.map(([label, value]) => (
                  <div key={label} className="flex justify-between py-2.5 text-sm">
                    <span className="text-neutral-500">{label}</span>
                    <span className="text-ink">{value}</span>
                  </div>
                ))}
              </div>
              <div className="pt-3">
                <a
                  href={`${DRUPAL_BASE}/user/${uid}/orders`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#003cc5] hover:text-[#0036b1] hover:underline"
                >
                  Manage subscription ↗
                </a>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function BillingPage() {
  const { user } = useAuth();
  const { data: licenses, isLoading: licensesLoading, isError: licensesError } = useLicenses();
  const activeLicense = licenses?.find((l) => l.state === 'active') ?? licenses?.[0] ?? null;
  const { data: payments = [], isLoading: paymentsLoading, isError: paymentsError } =
    usePaymentHistory(activeLicense?.originatingOrderId);

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-ink">Billing</h1>
        <p className="mt-1 text-sm text-neutral-500">Subscription and payment history</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">

        {/* Left: license */}
        <div className="lg:col-span-1">
          <LicenseCard licenses={licenses} isLoading={licensesLoading} isError={licensesError} uid={user?.id} />
        </div>

        {/* Right: payment history */}
        <div className="lg:col-span-2">
          <div className="rounded-2xl border border-neutral-200 bg-paper overflow-hidden">
            <div className="border-b border-neutral-200 px-5 py-3.5">
              <h2 className="text-sm font-semibold text-ink">Payment History</h2>
            </div>

            {paymentsLoading && (
              <div className="p-5 space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-10 animate-pulse rounded-lg bg-parchment" />
                ))}
              </div>
            )}

            {paymentsError && (
              <p className="p-8 text-center text-sm text-neutral-500">
                Unable to load payment history.
              </p>
            )}

            {!paymentsLoading && !paymentsError && payments.length === 0 && (
              <p className="p-8 text-center text-sm text-neutral-500">
                No payments on record.
              </p>
            )}

            {!paymentsLoading && payments.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-200 text-left text-xs uppercase tracking-wider text-neutral-500">
                      <th className="px-5 py-3">Date</th>
                      <th className="px-5 py-3">Amount</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="hidden px-5 py-3 sm:table-cell">Invoice</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-200">
                    {payments.map((p) => (
                      <tr key={p.id} className="hover:bg-parchment">
                        <td className="px-5 py-3 text-neutral-500 whitespace-nowrap">
                          {formatDate(p.completed)}
                        </td>
                        <td className="px-5 py-3 font-medium text-ink whitespace-nowrap">
                          {p.amount?.formatted ?? '—'}
                        </td>
                        <td className="px-5 py-3">{stateChip(p.state)}</td>
                        <td className="hidden px-5 py-3 font-mono text-xs sm:table-cell">
                          {p.drupalOrderId && p.drupalPaymentId ? (
                            <a
                              href={`${DRUPAL_BASE}/user/${user?.id}/orders/${p.drupalOrderId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#003cc5] hover:text-[#0036b1] hover:underline"
                            >
                              Order #{p.drupalOrderId}
                            </a>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
